import { useResourceTypeContext } from 'resourceTypeContext'
import useAppliedFilterValues from 'components/AppliedFilters/useAppliedFilterValues'
import {
    isNilOrEmpty,
    useGetControlsAccess,
    useApiGetWithInfiniteScroll,
} from 'packages/core'
import { useTargetPerson } from 'hooks'
import config from 'config'
import { useAxios } from 'packages/core'
import { useMutation } from 'react-query'
import { RESOURCE_TYPE_NAMES } from 'utils'

const getValue = (filter, contextValue) => {
    switch (filter.type) {
        case 'checkbox':
        case 'nullableBoolean':
        case 'thresholdSearchText':
        case 'text':
            if (filter.contextProp.startsWith('advancedSearch')) {
                return encodeURIComponent(
                    contextValue
                        .toString()
                        .split(',')
                        .map((item) => item.trim())
                        .join(' '),
                )
            } else return encodeURIComponent(contextValue)
        case 'tree':
        case 'autocomplete':
        default:
            return encodeURIComponent(
                contextValue[filter.queryParam.valueProp]
                    .toString()
                    .split(',')
                    .map((item) => item.trim())
                    .join(' '),
            )
    }
}

const buildQueryParams = (state, appliedFilterValues, currentResourceType) => {
    const queryParams = []
    const advancedSearchParamNames = []
    const advancedSearchParamTypes = []
    const advancedSearchParamValues = []

    const tags = appliedFilterValues.filter((f) => f.category === 'tags')

    if (tags.length > 0) {
        const commaSeparatedTagValues = tags.map((t) => t.label).join(',')
        queryParams.push(`tags=${commaSeparatedTagValues}`)
    }

    const checklistFilters = appliedFilterValues.filter(
        (f) => f.category === 'checklist',
    )

    if (checklistFilters.length > 0) {
        const contextProp = checklistFilters[0].contextProp
        const queryParam = checklistFilters[0].queryParam

        const checklist = []
        state[contextProp].forEach((i) => {
            checklist.push(i[queryParam.valueProp])
        })

        const commaSeparatedValues = checklist.join(',')
        queryParams.push(`${[queryParam.name]}=${commaSeparatedValues}`)
    }

    const nonTags = appliedFilterValues.filter(
        (f) => f.category !== 'tags' && f.category !== 'checklist',
    )

    nonTags.map((appliedFilter) => {
        if (appliedFilter.category === 'generic') {
            const filter = currentResourceType.filters.find(
                (f) => f.name === appliedFilter.name,
            )
            if (filter) {
                // if type prop is there, it's an advanced search.
                if (filter.queryParam.type) {
                    advancedSearchParamNames.push(filter.queryParam.name)
                    advancedSearchParamTypes.push(filter.queryParam.type)
                    advancedSearchParamValues.push(
                        getValue(filter, state[filter.contextProp]),
                    )
                } else {
                    queryParams.push(
                        `${filter.queryParam.name}=${getValue(
                            filter,
                            state[filter.contextProp],
                        )}`,
                    )
                }
            }
        } else if (appliedFilter.category === 'forms') {
            var advancedSearchFilter = currentResourceType.filters.find(
                (f) => f.type === 'advancedSearch',
            )
            if (
                advancedSearchFilter &&
                !isNilOrEmpty(advancedSearchFilter.forms)
            ) {
                const formsFilter = advancedSearchFilter.forms.filters.find(
                    (f) => f.name === appliedFilter.name,
                )
                if (formsFilter) {
                    if (formsFilter.queryParam.type) {
                        advancedSearchParamNames.push(
                            formsFilter.queryParam.name,
                        )
                        advancedSearchParamTypes.push(
                            formsFilter.queryParam.type,
                        )
                        advancedSearchParamValues.push(
                            getValue(
                                formsFilter,
                                state[formsFilter.contextProp],
                            ),
                        )
                    } else {
                        queryParams.push(
                            `${formsFilter.queryParam.name}=${getValue(
                                formsFilter,
                                state[formsFilter.contextProp],
                            )}`,
                        )
                    }
                }
            }
        }
        return false
    })

    if (advancedSearchParamNames.length > 0) {
        queryParams.push(
            `advancedSearchParamNames=${advancedSearchParamNames.join(',')}`,
        )
        queryParams.push(
            `advancedSearchParamValues=${advancedSearchParamValues.join(',')}`,
        )
        queryParams.push(
            `advancedSearchParamTypes=${advancedSearchParamTypes.join(',')}`,
        )
    }

    return queryParams
}

const useResourceTypeApi = (currentResourceType) => {
    const controls = useGetControlsAccess()

    const [targetPerson] = useTargetPerson()
    const [state] = useResourceTypeContext()

    const appliedFiltersValues = useAppliedFilterValues(currentResourceType)

    const queryParams = buildQueryParams(
        state,
        appliedFiltersValues,
        currentResourceType,
    )

    if (targetPerson) {
        queryParams.push(`targetPersonId=${targetPerson.id}`)
    }

    if (state.searchTerm) {
        queryParams.push(`searchTerm=${encodeURIComponent(state.searchTerm)}`)
    }

    if (state.accountStoreUsageType) {
        queryParams.push(
            `accountStoreUsageTypeId=${state.accountStoreUsageType.id}`,
        )
    }

    if (
        state.accountStore &&
        currentResourceType.name !== RESOURCE_TYPE_NAMES.BUSINESS_ROLES
    ) {
        queryParams.push(`accountStoreId=${state.accountStore.id}`)
    }
    if (
        state.accountStore &&
        state.tCode &&
        currentResourceType.name === RESOURCE_TYPE_NAMES.BUSINESS_ROLES
    ) {
        queryParams.push(
            `accountStoreId=${state.accountStore.id}&tCode=${state.tCode?.id}`,
        )
    }

    if (state.shouldLoadTags) {
        queryParams.push('includeTagsResponse=true')
    }

    if (currentResourceType.name === 'ApplicationRoles') {
        const hasAccessToSuggestedApplicationRolesControl =
            controls.findIndex(
                (c) => c.name === 'ITShop-SuggestedApplicationRoles-Control',
            ) >= 0

        if (
            !hasAccessToSuggestedApplicationRolesControl &&
            config &&
            config.FETCH_SUGGESTED_APPLICATION_ROLES_ALWAYS?.toLowerCase() ===
            'true'
        ) {
            queryParams.push('showSuggested=true')
        }
    }

    return queryParams.join('&')
}

const useRequestAccessResources = (currentResourceType, enabled = true) => {
    const [{ sorting: sortQuery }] = useResourceTypeContext()

    let queryString = useResourceTypeApi(currentResourceType)

    if (sortQuery.sortOrder) {
        queryString = `${queryString}&sortBy=${sortQuery.sortBy}&desc=${sortQuery.sortOrder === 'desc' && true
            }`
    }

    return useApiGetWithInfiniteScroll(
        currentResourceType.dataSource.url,
        queryString,
        enabled,
    )
}

export const useActivateNow = () => {
    const callApi = useAxios()

    const getApiUrl = (
        dataSourceUrl,
        resourceId,
        targetPersonPersonId,
        resourceAssignmentId,
        timeConstraintDetails,
        locationId,
    ) => {
        let url = ''
        if (dataSourceUrl?.includes('businessRoles')) {
            url = `${dataSourceUrl}/activate/${resourceAssignmentId}/${targetPersonPersonId}`
        } else {
            url = `${dataSourceUrl}/activate/${resourceId}/${targetPersonPersonId}`
            const params = []
            if (locationId) {
                params.push(`locationId=${locationId}`)
            }
            if (resourceAssignmentId) {
                params.push(`resourceAssignmentId=${resourceAssignmentId}`)
            }
            if (params.length > 0) {
                url += `?${params.join('&')}`
            }
        }

        if (timeConstraintDetails) {
            const params = []
            if (timeConstraintDetails.startDateUtc) {
                params.push(`startDate=${timeConstraintDetails.startDateUtc}`)
            }
            if (timeConstraintDetails.endDateUtc) {
                params.push(`endDate=${timeConstraintDetails.endDateUtc}`)
            }
            if (params.length > 0) {
                url += (url.includes('?') ? '&' : '?') + params.join('&')
            }
        }
        return url
    }

    return useMutation((data) =>
        callApi({
            method: 'PATCH',
            url: getApiUrl(
                data.resourceType.dataSource.url,
                data.resourceId,
                data.targetPersonPersonId,
                data?.resourceAssignmentId,
                data?.timeConstraintDetails,
                data?.locationId,
            ),
        }).then((retult) => retult),
    )
}

export default useRequestAccessResources
